import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, RefreshCw, Copy, Check, AlertTriangle, ShieldCheck, Mail, Eye, EyeOff, User, Lock, Pencil, X,
  Sparkles, ChevronDown, PlayCircle, RotateCcw, Boxes,
} from 'lucide-react';
import { getMe, regenerateMyKey, changeMyPassword, updateMyProfile, getPublicConfig, getKeyModels } from '../api';
import PasswordInput from '../components/PasswordInput';

function Card({ icon: Icon, title, children, className = '' }) {
  return (
    <div className={`bg-panel border border-panel-border rounded-xl p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-orange" />
        <h2 className="text-fg font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ModelsCard({ apiKey }) {
  const [models, setModels] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let live = true;
    setModels(null); setFailed(false);
    getKeyModels(apiKey)
      .then((r) => { if (live) setModels((r.data?.data || []).map((m) => m.id).sort()); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [apiKey]);

  const copy = (id) => {
    navigator.clipboard?.writeText(id);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 1500);
  };

  return (
    <Card icon={Boxes} title="Models you can use" className="lg:col-span-3">
      <p className="text-fg-muted text-sm mb-2">
        What your API key may call: pass one as <code className="text-fg font-mono">model</code>, or{' '}
        <code className="text-fg font-mono">agent.model</code> for an agent session.
      </p>
      <p className="text-xs text-fg-muted mb-4 flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        <span>
          These are your <strong className="text-fg">default organization</strong>&apos;s models, whichever organization
          is selected here. Your key works in every organization you belong to, and each can allow different models:
          send <code className="text-fg font-mono whitespace-nowrap">X-Org-Id</code> to use another one.
        </span>
      </p>
      {failed ? (
        <p className="text-sm text-red-400">Couldn&apos;t load the models for this key.</p>
      ) : models === null ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : models.length === 0 ? (
        <p className="text-sm text-fg-muted">No models are available to this key. Ask an administrator to add some to your tier.</p>
      ) : (
        // A long allow-list scrolls inside the card instead of pushing the page down.
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
          {models.map((id) => (
            <li key={id} className="flex items-center gap-2 min-w-0">
              <code className="flex-1 min-w-0 px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono truncate" title={id}>
                {id}
              </code>
              <button onClick={() => copy(id)} title="Copy" aria-label={`Copy ${id}`}
                className="p-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg transition-colors">
                {copied === id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ProfileCard({ me, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {kind, text}

  const startEdit = () => {
    setName(me?.name || '');
    setEmail(me?.email || '');
    setMsg(null);
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName) return setMsg({ kind: 'err', text: 'Name cannot be empty' });
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) return setMsg({ kind: 'err', text: 'Enter a valid email' });

    const payload = {};
    if (trimmedName !== me?.name) payload.name = trimmedName;
    if (trimmedEmail !== me?.email) payload.email = trimmedEmail;
    if (!Object.keys(payload).length) { setEditing(false); return; }

    setBusy(true);
    try {
      const r = await updateMyProfile(payload);
      localStorage.setItem('admin_email', r.data.email);
      localStorage.setItem('admin_name', r.data.name || '');
      onSaved({ email: r.data.email, name: r.data.name });
      setMsg({ kind: 'ok', text: 'Profile updated.' });
      setEditing(false);
    } catch (e2) {
      setMsg({ kind: 'err', text: e2.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={User} title="Profile" className="lg:col-span-1">
      {msg && (
        <div className={`mb-4 p-2.5 rounded-lg border text-xs ${
          msg.kind === 'ok'
            ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
            : 'bg-red-500/10 border-red-500/25 text-red-400'
        }`}>
          {msg.text}
        </div>
      )}

      {editing ? (
        <form onSubmit={save} className="space-y-4 text-sm">
          <div>
            <label className="block text-xs text-fg-muted mb-1.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange transition-colors"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-1.5 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange transition-colors"
              placeholder="you@company.com"
            />
            <p className="mt-1 text-[11px] text-fg-muted">This is also your sign-in email.</p>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={busy}
              className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="px-3 py-2 text-fg-muted hover:text-fg text-sm transition-colors inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs text-fg-muted flex items-center gap-1"><Mail className="w-3 h-3" /> Email</div>
            <div className="text-fg mt-0.5 break-all">{me?.email}</div>
          </div>
          <div>
            <div className="text-xs text-fg-muted">Name</div>
            <div className="text-fg mt-0.5">{me?.name || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-fg-muted">Role</div>
            <div className="mt-1">
              {me?.is_admin
                ? <span className="px-2 py-0.5 bg-orange/20 text-orange rounded text-xs inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Admin</span>
                : <span className="px-2 py-0.5 bg-fg/10 text-fg-muted rounded text-xs">User</span>}
            </div>
          </div>
          <div>
            <div className="text-xs text-fg-muted">Tier</div>
            <div className="text-fg mt-0.5">{me?.tier || '—'}</div>
          </div>
          <button onClick={startEdit}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 bg-panel border border-panel-border text-fg hover:border-orange rounded-lg text-sm transition-colors">
            <Pencil className="w-3.5 h-3.5" /> Edit profile
          </button>
        </div>
      )}
    </Card>
  );
}

function SetupPrompt({ base, apiKey }) {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(false);
  const prompt = `Set up LatentStack on this machine for me, end-to-end. Run the steps, verify each one, and do NOT print my full API key back to me.

1. Install the latentcode CLI and the LatentWork desktop app with one command:
   - Linux / macOS:        curl -fsSL ${base}/install.sh | bash
   - Windows (PowerShell):  irm ${base}/install.ps1 | iex
   (The latentcode CLI is a single self-contained binary — no Node.js/npm needed.)
2. Point latentcode at the LatentStack gateway and set my key:
   latentcode config set base-url ${base}/v1
   latentcode config set api-key ${apiKey}
3. Verify the CLI: run "latentcode models" and confirm my models are listed (or send one test request to model "anthropic/claude-haiku-4-5" and confirm it succeeds).
4. Confirm the LatentWork desktop app appears in my app menu / Launchpad / Start Menu.
5. Summarize exactly what you changed and confirm everything works.`;
  const copyPrompt = () => { navigator.clipboard?.writeText(prompt); setCopied(true); setTimeout(() => setCopied(false), 2400); };

  const steps = [
    'Installs the latentcode CLI + LatentWork desktop app',
    'Sets your gateway URL & API key',
    'Verifies the CLI works',
    'Confirms the desktop app is installed',
  ];

  return (
    <Card icon={Sparkles} title="One-paste setup" className="lg:col-span-3">
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 items-center">
        <div>
          <p className="text-fg-muted text-sm mb-4 max-w-xl">
            Skip the manual steps. Copy one prompt, paste it into{' '}
            <span className="text-fg font-medium">Claude Code</span> (or any terminal AI agent),
            and it sets everything up for you — verified, with no typos.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-fg">
                <span className="shrink-0 h-5 w-5 rounded-full bg-orange/15 text-orange text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                {s}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 lg:w-60">
          <button
            onClick={copyPrompt}
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-colors ${
              copied
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-orange hover:bg-orange-hover text-white shadow-lg shadow-orange/25'
            }`}
          >
            {copied
              ? <><Check className="w-4 h-4" /> Copied — paste it in</>
              : <><Sparkles className="w-4 h-4" /> Copy setup prompt</>}
          </button>
          <button
            onClick={() => setPreview((p) => !p)}
            className="inline-flex items-center justify-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${preview ? 'rotate-180' : ''}`} />
            {preview ? 'Hide prompt' : 'Preview prompt'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-5 rounded-lg border border-panel-border bg-[#0c0d10] overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 text-[11px] font-mono text-gray-500">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
            <span className="ml-2">setup-prompt.txt</span>
          </div>
          <pre className="px-4 py-4 text-xs leading-relaxed font-mono overflow-x-auto whitespace-pre-wrap text-gray-200">{prompt}</pre>
        </div>
      )}
    </Card>
  );
}

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState(null); // {kind, text}
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) return setMsg({ kind: 'err', text: 'New password must be at least 8 characters' });
    if (next !== confirm) return setMsg({ kind: 'err', text: 'Passwords do not match' });
    setBusy(true);
    try {
      await changeMyPassword(current, next);
      setMsg({ kind: 'ok', text: 'Password updated.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e2) {
      setMsg({ kind: 'err', text: e2.response?.data?.detail || 'Failed to update password' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-panel border border-panel-border rounded-xl lg:col-span-3 overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-6 py-5 hover:bg-fg/[0.02] transition-colors">
        <span className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-orange" />
          <span className="text-fg font-semibold">Change password</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-fg-muted shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-6">
          {msg && (
            <div className={`mb-4 p-3 rounded-lg border text-sm ${
              msg.kind === 'ok'
                ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                : 'bg-red-500/10 border-red-500/25 text-red-400'
            }`}>
              {msg.text}
            </div>
          )}
          <form onSubmit={submit} className="space-y-4 max-w-md">
            <div>
              <label className="block text-xs text-fg-muted mb-1.5">Current password</label>
              <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" />
            </div>
            <div>
              <label className="block text-xs text-fg-muted mb-1.5">New password</label>
              <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
            </div>
            <div>
              <label className="block text-xs text-fg-muted mb-1.5">Confirm new password</label>
              <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" />
            </div>
            <button type="submit" disabled={busy}
              className="px-4 py-2.5 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50">
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

const TOUR_KEY = 'lstack_walkthrough_done';

function TourCard() {
  const navigate = useNavigate();
  const [done, setDone] = useState(!!localStorage.getItem(TOUR_KEY));

  const replay = () => {
    localStorage.removeItem(TOUR_KEY);
    setDone(false);
    window.dispatchEvent(new CustomEvent('lstack:open-tour'));
    navigate('/');
  };

  return (
    <Card icon={PlayCircle} title="Product Tour">
      <p className="text-fg-muted text-sm mb-4">
        Take a guided walkthrough of LatentStack's key features.
      </p>
      <button
        onClick={replay}
        className="flex items-center gap-2 px-4 py-2 bg-panel border border-panel-border text-fg hover:border-orange rounded-lg text-sm transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        {done ? 'Replay tour' : 'Start tour'}
      </button>
    </Card>
  );
}

export default function Account() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); 
  const [copied, setCopied] = useState(false);
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  // Gateway base URL clients should use. Defaults to this origin; overridden by
  // the backend's PUBLIC_BASE_URL (per-deployment) so the setup prompt stays correct
  // even when the UI and gateway live on different hosts.
  const [baseUrl, setBaseUrl] = useState(window.location.origin);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMe((await getMe()).data); }
    catch { setError('Failed to load your account'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getPublicConfig()
      .then((r) => { if (r.data?.base_url) setBaseUrl(r.data.base_url); })
      .catch(() => { /* keep origin fallback */ });
  }, []);

  const regenerate = async () => {
    if (!window.confirm('Regenerate your API key? Your current key stops working immediately.')) return;
    setBusy(true); setError(null);
    try { const r = await regenerateMyKey(); setMe((m) => ({ ...m, api_key: r.data.api_key, has_api_key: true })); setShow(true); }
    catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const key = me?.api_key || '';
  const masked = key ? `${key.slice(0, 8)}${'•'.repeat(Math.max(0, key.length - 12))}${key.slice(-4)}` : '';
  const copy = () => { navigator.clipboard?.writeText(key); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (loading) return <div className="text-center text-fg-muted py-16">Loading…</div>;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg">Settings</h1>
        <p className="text-fg-muted text-sm mt-1">Your account, API key and security.</p>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Profile */}
        <ProfileCard me={me} onSaved={(p) => setMe((m) => ({ ...m, ...p }))} />

        {/* API Key */}
        <Card icon={KeyRound} title="API Key" className="lg:col-span-2">
          <p className="text-fg-muted text-sm mb-4">
            Use as an OpenAI-compatible endpoint — header <code className="text-fg font-mono">Authorization: Bearer &lt;key&gt;</code>.
          </p>

          {me?.has_api_key ? (
            <>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-4 py-3 bg-field border border-field-border rounded-lg text-orange text-sm font-mono break-all">
                  {show ? key : masked}
                </code>
                <button onClick={() => setShow((s) => !s)} title={show ? 'Hide' : 'Show'}
                  className="p-3 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg transition-colors">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={copy} title="Copy"
                  className="flex items-center gap-1 px-4 py-3 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm transition-colors">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button onClick={regenerate} disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 bg-panel border border-panel-border text-fg hover:border-orange rounded-lg text-sm disabled:opacity-50 transition-colors">
                  <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Regenerate
                </button>
                <span className="text-xs text-fg-muted flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Regenerating invalidates the current key immediately.
                </span>
              </div>
            </>
          ) : (
            <button onClick={regenerate} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm disabled:opacity-50">
              <KeyRound className="w-4 h-4" /> Generate API key
            </button>
          )}
        </Card>

        {/* One-paste setup — installs the latentcode CLI + the LatentWork desktop app
            via the single install.sh|ps1 command, then configures the gateway. */}
        {me?.has_api_key && key && <ModelsCard apiKey={key} />}

        {me?.has_api_key && <SetupPrompt base={baseUrl} apiKey={key} />}

        {/* Change password (collapsible) */}
        <ChangePassword />

        {/* Tour */}
        <TourCard />
      </div>
    </div>
  );
}
